// Products a person confirmed for a show's garments, laid over the pipeline's catalog matches.
//
// A confirmed product is pinned to WHERE the garment is in the clip (frame ranges and the hotspot's
// mean position), not to a track id: a pipeline rerun renumbers tracks, and the same rule that moves an
// admin's choice (best frame overlap) moves these. The client page and the curation page both read
// them through `confirmedProductFor`, so the two never disagree.
import type { PublishedClip, PublishedProduct, PublishedTrack } from '../utils/clipApi';

export interface GarmentRef {
  /** [first, last] frame ranges the garment is on screen (one per shot it appears in). */
  frames: [number, number][];
  /** Mean hotspot position, normalised to the frame: tells two garments on one person apart. */
  at: [number, number];
  /** Move this garment's "+" off the point the pipeline tracked, as [dx, dy] normalised to the frame
   *  (+x right, +y down). The hotspot still follows the track: every frame is shifted by the same amount. */
  offset?: [number, number];
  /** Other garments on the same tracked person that the detector never separates (the cardigan over
   *  a dress). Each gets its own "+", following this track at its own offset (Josh, 2026-10-22: a "+"
   *  for the dress and one for the cardigan). */
  companions?: { product: PublishedProduct; offset: [number, number] }[];
}

export interface ConfirmedProduct {
  product: PublishedProduct;
  garments: GarmentRef[];
  /** Similar styles a person picked for this garment: the drawer's "See similar items" and the card's
   *  "Similar" button. Garments worn with it get their own "+" (`companions`), not a slot here: a
   *  product listed with no "+" on the picture read as confusing (Josh, 2026-10-22). */
  similar?: PublishedProduct[];
}

interface TrackLike {
  id: number;
  start_frame: number;
  end_frame: number;
  segments?: [number, number][];
  path: [number, number, number][];
}

const overlap = (a: [number, number], b: [number, number]) => {
  const inter = Math.min(a[1], b[1]) - Math.max(a[0], b[0]) + 1;
  if (inter <= 0) return 0;
  const union = Math.max(a[1], b[1]) - Math.min(a[0], b[0]) + 1;
  return inter / union;
};

/** Best temporal IoU between any of the track's segments and any of the reference's ranges. */
const frameOverlap = (track: TrackLike, ref: GarmentRef) => {
  const segments = track.segments?.length ? track.segments : [[track.start_frame, track.end_frame] as [number, number]];
  let best = 0;
  for (const s of segments) for (const r of ref.frames) best = Math.max(best, overlap(s, r));
  return best;
};

const meanPosition = (track: TrackLike): [number, number] => {
  if (!track.path.length) return [0.5, 0.5];
  let x = 0;
  let y = 0;
  for (const row of track.path) {
    x += row[1];
    y += row[2];
  }
  return [x / track.path.length, y / track.path.length];
};

/** The confirmed product for a track: the reference it overlaps by at least half, nearest in position
 *  when several do (the tee and the shorts share the same frames). Null when none. */
/** How far a track's hotspot may sit from a confirmed garment's and still be that garment, as a
 *  fraction of the frame. Without it, frame overlap alone decided, so EVERY garment detected in a
 *  confirmed garment's shot took its product: the woman beside Tabby in the S02E01 flashback wore the
 *  blouse picked for Tabby, and Dr. Sullivan's blazer became Imogen's dress (jaysilvr's review of #108).
 *  Measured on the two published clips: a garment's own hotspot lands within 0.07 of its reference,
 *  another person's starts at 0.20, so 0.18 separates them with room on both sides. */
export const MAX_HOTSPOT_DISTANCE = 0.18;

export function confirmedEntryFor(
  confirmed: ConfirmedProduct[],
  track: TrackLike,
  minOverlap = 0.5,
  maxDistance = MAX_HOTSPOT_DISTANCE,
): ConfirmedProduct | null {
  return confirmedMatchFor(confirmed, track, minOverlap, maxDistance)?.entry ?? null;
}

/** The confirmed entry for a track, the garment reference it matched (its offset and companions) and
 *  how well (frame overlap minus distance). */
function confirmedMatchFor(
  confirmed: ConfirmedProduct[],
  track: TrackLike,
  minOverlap = 0.5,
  maxDistance = MAX_HOTSPOT_DISTANCE,
): { entry: ConfirmedProduct; ref: GarmentRef; score: number } | null {
  const [tx, ty] = meanPosition(track);
  let best: { entry: ConfirmedProduct; ref: GarmentRef; score: number } | null = null;
  for (const entry of confirmed) {
    for (const ref of entry.garments) {
      const iou = frameOverlap(track, ref);
      if (iou < minOverlap) continue;
      const distance = Math.hypot(ref.at[0] - tx, ref.at[1] - ty);
      if (distance > maxDistance) continue;
      // Frames first (a different shot is a different garment), then the closer hotspot.
      const score = iou - distance;
      if (!best || score > best.score) best = { entry, ref, score };
    }
  }
  return best;
}

const overlapsInTime = (a: TrackLike, b: TrackLike) => a.start_frame <= b.end_frame && b.start_frame <= a.end_frame;

export function confirmedProductFor(
  confirmed: ConfirmedProduct[],
  track: TrackLike,
  minOverlap = 0.5,
  maxDistance = MAX_HOTSPOT_DISTANCE,
): PublishedProduct | null {
  return confirmedEntryFor(confirmed, track, minOverlap, maxDistance)?.product ?? null;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** The track's path moved by [dx, dy], kept inside the frame. */
const shiftPath = (path: [number, number, number][], [dx, dy]: [number, number]): [number, number, number][] =>
  path.map(([f, x, y]) => [f, clamp01(x + dx), clamp01(y + dy)]);

// Ids for companion hotspots: well clear of the pipeline's track ids (which count up from 0), and
// stable across reloads so React keys and "which + is open" survive a re-render.
const COMPANION_ID_BASE = 1_000_000;

/** The published clip with the confirmed products on their garments; everything else untouched. */
export function curateWith(confirmed: ConfirmedProduct[]) {
  return (clip: PublishedClip): PublishedClip => {
    const matches = new Map(clip.tracks.map((track) => [track, confirmedMatchFor(confirmed, track)]));
    // One garment, one "+". A rerun can find the same garment twice at once (the 2026-10-23 12:49Z run:
    // tracks 3 and 4 both on Kelly in her first shot), and each copy used to bring its own dress and
    // cardigan hotspots (SIL-1592: "remove additional hotspots"). Of the tracks claiming one garment at
    // the same time, the best match keeps it and the others are dropped.
    const outranked = (track: PublishedTrack) => {
      const mine = matches.get(track);
      return clip.tracks.some((other) => {
        const theirs = other !== track ? matches.get(other) : null;
        return theirs && mine && theirs.ref === mine.ref && overlapsInTime(track, other) && (theirs.score > mine.score || (theirs.score === mine.score && other.id < track.id));
      });
    };
    return {
      ...clip,
      tracks: clip.tracks.flatMap((track) => {
        const match = matches.get(track);
        if (!match) return [track];
        if (outranked(track)) return [];
        const { entry, ref } = match;
        // A confirmed garment carries no catalog "similar" items (they were neighbours of the wrong
        // product), only the similar styles a person picked for it. With none picked it keeps whatever
        // the clip was published with, which is where a model's similar items arrive
        // (silvr-api-endpoint #122, live since 14:24Z): those are computed FROM this product's own
        // photo, so they belong to this garment, and an empty list threw them away before the client
        // saw them (jaysilvr's review of #122).
        const main = { ...track, product: entry.product, alternates: entry.similar?.length ? entry.similar : track.alternates ?? [], path: ref.offset ? shiftPath(track.path, ref.offset) : track.path };
        // A companion is its own confirmed garment: the similar styles picked for that product, if any.
        const companions = (ref.companions ?? []).map((companion, i) => ({
          ...track,
          id: COMPANION_ID_BASE + track.id * 10 + i,
          product: companion.product,
          // NOT track.alternates: this "+" is a different garment riding on the main one's track, and
          // those published items were computed from the MAIN product's photo, so the dress companion
          // would offer cardigans (jaysilvr's review of #108). Only a hand-picked list belongs here.
          alternates: confirmed.find((other) => other.product === companion.product)?.similar ?? [],
          path: shiftPath(track.path, companion.offset),
        }));
        return [main, ...companions];
      }),
    };
  };
}
